import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import { useQuery } from '@tanstack/react-query';
import { useIntegrationService } from '../../services/integration.service';
import { Platform } from '../../services/integration.types';
import IntegrationCreateComponent from './IntegrationCreate';
import Breadcrumb from '../common/Breadcrumb';
import PageLayout from '../common/PageLayout';

const IntegrationCreatePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { platformName } = useParams<{ platformName: string }>();
    const [searchParams] = useSearchParams();
    const importType = searchParams.get('type') || '';
    
    const { fetchPlatforms } = useIntegrationService();
    
    const { data: platforms, isLoading } = useQuery({
        queryKey: ['platforms'],
        queryFn: fetchPlatforms,
        staleTime: 5 * 60 * 1000,
    });

    // Handle special case for file upload (excelUpload)
    let selectedPlatform = platforms?.find(p => p.name === platformName);

    // Create a dummy platform for file upload if platformName is 'excelUpload'
    if (platformName === 'excelUpload' && !selectedPlatform) {
        selectedPlatform = {
            name: 'excelUpload',
            display_name: t('integrations.selectModal.fileUpload'),
            platform_type: 'excelUpload',
            logo_uri: '',
            legacy: false,
        } as Platform;
    }

    const handleJobCreated = () => {
        navigate('/integrations');
    };

    const handleBack = () => {
        navigate('/integrations/add');
    };

    const breadcrumbItems = [
        { label: t('integrations.title'), path: '/integrations' },
        { label: t('integrations.selectPlatform'), path: '/integrations/add' },
        { label: selectedPlatform?.display_name || platformName || '' }
    ];

    if (isLoading) {
        return (
            <PageLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </PageLayout>
        );
    }

    if (!selectedPlatform) {
        return (
            <PageLayout>
                <Breadcrumb items={breadcrumbItems} />
                <Typography variant="h6" sx={{ mt: 3 }}>
                    {t('integrations.platformNotFound')}
                </Typography>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 3 }}>
                <IntegrationCreateComponent
                    platform={selectedPlatform}
                    selectedImportType={importType}
                    onJobCreated={handleJobCreated}
                />
            </Box>
        </PageLayout>
    );
};

export default IntegrationCreatePage;